// Global Instructions Rule Applied!
import { useCallback } from 'react'
import { useSelector } from 'react-redux'
import { createSelector } from '@reduxjs/toolkit'
import type { RootState } from '../../../state_manager/store.ts'
import type { LookupHeadersModel } from '../../../state_manager/data_models/lookup_headers_model.ts'
import type { LookupDetailsModel } from '../../../state_manager/data_models/lookup_details_model.ts'

/**
 * Transforms lookup_headers from globalConfigs into the format expected by components
 * Creates deep copies to avoid mutating Redux state
 */
function transformLookupHeaders(lookupHeaders: LookupHeadersModel[] = []) {
  const headers = lookupHeaders.map(({ id, category, is_active }) => ({ id, category, is_active }))
  const details: LookupDetailsModel[] = []
  const detailsByCategory: Record<string, LookupDetailsModel[]> = {}
  const detailsByHeaderId: Record<string, LookupDetailsModel[]> = {}

  lookupHeaders.forEach((header) => {
    if (header.details && Array.isArray(header.details)) {
      // Create deep copies of details with header_id added for compatibility
      // This ensures we don't mutate the original state
      const headerDetails = header.details.map((detail) => ({
        ...detail,
        header_id: header.id
      }))

      // Create new arrays to avoid reference sharing
      details.push(...headerDetails)
      detailsByCategory[header.category] = [...headerDetails]
      detailsByHeaderId[header.id] = [...headerDetails]
    }
  })

  return { headers, details, detailsByCategory, detailsByHeaderId }
}

/**
 * Selector to get all lookup data transformed
 */
const selectLookups = createSelector(
  [(state: RootState) => state.globalConfigs?.lookup_headers || []],
  (lookupHeaders: LookupHeadersModel[]) => {
    return transformLookupHeaders(lookupHeaders)
  }
)

/**
 * Selector to get lookup options for a specific category
 * Returns options in the format: [{ value: id, label: label, sortOrder: sort_order }]
 */
export const selectLookupOptions = createSelector(
  [(state: RootState) => state.globalConfigs?.lookup_headers || [], (_: RootState, category: string) => category],
  (lookupHeaders: LookupHeadersModel[], category: string) => {
    const header = lookupHeaders.find((h) => h.category === category)
    if (!header || !header.details) {
      return []
    }

    // Create a copy of the array before sorting to avoid mutating state
    return [...header.details]
      .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
      .map(toLookupOption)
  }
)

export interface LookupOption {
  value: string
  label: string
  sortOrder: number | null | undefined
  /** Summary line rendered under the option while choosing. Only the Critical
   *  Technology Area group carries these today; every other lookup leaves both
   *  undefined and its options render exactly as before. */
  shortDescription?: string | null
  /** The full published definition, for the field's help "?" and the tooltip on
   *  the selected value. */
  description?: string | null
}

/** One lookup detail as a select option. The single place the detail -> option
 *  mapping lives, so the selector and the hook cannot drift. */
function toLookupOption(detail: LookupDetailsModel): LookupOption {
  return {
    value: detail.id,
    label: detail.label,
    sortOrder: detail.sort_order,
    shortDescription: detail.short_description,
    description: detail.description
  }
}

export interface UseLookupsReturn {
  // Data
  detailsByCategory: Record<string, LookupDetailsModel[]>
  loading: boolean

  // Helper functions
  getLookupOptions: (
    category: string,
    conditionalHeaderId?: string | null,
    conditionalDetailIds?: string[] | null
  ) => LookupOption[]
  getLookupDetailById: (detailId: string) => LookupDetailsModel | undefined
  getHeaderByCategory: (category: string) => { id: string; category: string; is_active: boolean } | undefined
  getDetailsByCategory: (category: string) => LookupDetailsModel[]
  getLookupLabel: (detailId: string) => string
  getLookupIdByLabel: (label: string, category?: string | null) => string | null
}

/**
 * Custom hook for managing lookup data
 * Automatically fetches lookup data when user is authenticated
 */
export const useLookups = (): UseLookupsReturn => {
  const lookupHeaders = useSelector((state: RootState) => state.globalConfigs?.lookup_headers || [])
  const { details, detailsByCategory, headers } = useSelector(selectLookups)
  const isLoadingEssentialData = useSelector((state: RootState) => state.auth?.isLoadingEssentialData || false)

  // Loading is true if essential data is being loaded or if lookup headers are empty
  const loading = isLoadingEssentialData || lookupHeaders.length === 0

  // Get lookup options for a category (for select components)
  const getLookupOptions = useCallback(
    (
      category: string,
      conditionalHeaderId: string | null = null,
      conditionalDetailIds: string[] | null = null
    ): LookupOption[] => {
      const categoryDetails = detailsByCategory[category] || []
      // Ensure details is an array and create a copy before sorting
      let detailsArray = Array.isArray(categoryDetails) ? [...categoryDetails] : []

      // Apply conditional filtering if parameters are provided
      if (conditionalHeaderId && conditionalDetailIds && Array.isArray(conditionalDetailIds)) {
        detailsArray = detailsArray.filter((detail) => {
          // Check if the detail has the matching conditional_header_id
          if (detail.conditional_header_id !== conditionalHeaderId) {
            return false
          }

          // Check if at least one conditional_detail_id matches
          if (!detail.conditional_detail_ids || !Array.isArray(detail.conditional_detail_ids)) {
            return false
          }

          // Check for intersection between conditional_detail_ids and provided conditionalDetailIds
          return detail.conditional_detail_ids.some((id) => conditionalDetailIds.includes(id))
        })
      }

      return detailsArray.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)).map(toLookupOption)
    },
    [detailsByCategory]
  )

  // Get lookup detail by ID
  const getLookupDetailById = useCallback(
    (detailId: string): LookupDetailsModel | undefined => {
      return details.find((detail) => detail.id === detailId)
    },
    [details]
  )

  // Get lookup header by category
  const getHeaderByCategory = useCallback(
    (category: string): { id: string; category: string; is_active: boolean } | undefined => {
      return headers.find((header) => header.category === category)
    },
    [headers]
  )

  // Get lookup details by category name
  const getDetailsByCategory = useCallback(
    (category: string): LookupDetailsModel[] => {
      return detailsByCategory[category] || []
    },
    [detailsByCategory]
  )

  // Get lookup label by ID
  const getLookupLabel = useCallback(
    (detailId: string): string => {
      const detail = details.find((d) => d.id === detailId)
      return detail ? detail.label : ''
    },
    [details]
  )

  // Get lookup ID by label (reverse of getLookupLabel)
  const getLookupIdByLabel = useCallback(
    (label: string, category: string | null = null): string | null => {
      if (!label) return null

      let searchDetails = details

      // If category is specified, filter to that category first
      if (category && detailsByCategory[category]) {
        searchDetails = detailsByCategory[category]
      }

      const detail = searchDetails.find((d) => d.label === label)
      return detail ? detail.id : null
    },
    [details, detailsByCategory]
  )

  return {
    // Data
    detailsByCategory,
    loading,

    // Helper functions
    getLookupOptions,
    getLookupDetailById,
    getHeaderByCategory,
    getDetailsByCategory,
    getLookupLabel,
    getLookupIdByLabel
  }
}
